import { Provider } from "@angular/core";
import { IListComponentOptions } from "../interfaces/list-component-options";
import {
    LIST_DATA_SERVICE, LIST_STATE_SERVICE
} from "../constants/tokens";
import { ListDataSourceService } from "../services/list-data-source.service";
import { InvalidParametersException } from "../../exceptions/invalid-parameters-exception";

/**
 * Utility class providing helper methods for list operations and configuration.
 *
 * This class offers static methods for:
 * - Pagination calculations
 * - Angular provider setup for list components
 * - List view configuration validation
 *
 * It's designed to be used with the table module's components and services
 * to simplify common list-related tasks.
 */
export class ListUtil {

    /**
     * Calculates the starting index for pagination based on page number and page size.
     *
     * @param page - The current page number (0-based)
     * @param pageSize - The number of items per page
     * @returns The starting index for the current page, or undefined if pageSize is undefined or 0
     */
    static getFromValue(page?: number, pageSize?: number): number {

        if (!pageSize) {
            return undefined;
        }
        if (!page) {
            return 0;
        }

        return page * pageSize;
    }

    /**
     * Creates an array of Angular providers needed for list components.
     *
     * @param options - Configuration options for the list
     * @param options.listStateService - Service that manages list state
     * @param options.listDataService - Service that provides list data
     * @returns Array of providers for dependency injection
     * @throws InvalidParametersException if required services are missing
     */
    static getListComponentProviders(options: IListComponentOptions): Array<Provider> {

        if (!options?.listStateService) {
            throw new InvalidParametersException("options.listStateService is required");
        }
        if (!options.listDataService) {
            throw new InvalidParametersException("options.listDataService is required");
        }
        return [
            {
                provide: LIST_STATE_SERVICE,
                useExisting: options.listStateService
            },
            {
                provide: LIST_DATA_SERVICE,
                useExisting: options.listDataService
            },
            ListDataSourceService
        ];
    }

    /**
     * Converts a kebab-case model name into a human-readable Title Case label.
     *
     * @param modelName - The kebab-case model name (e.g. `'batch-product'`), or undefined
     * @returns A Title Case string (e.g. `'Batch Product'`), or an empty string if the
     * input is falsy
     */
    static modelLabel(modelName: string | undefined): string {
        if (!modelName) {
            return "";
        }
        return modelName
            .split("-")
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(" ");
    }

    /**
     * Unwraps a row to get the original model if it has been localized.
     *
     * @param row - The row to unwrap, potentially containing an originalModel property
     * @returns The original model if present, otherwise the row itself
     * @template T - The type of the original model
     */
    static unwrapRow<T>(row: unknown): T {
        if (row && typeof row === "object" && "originalModel" in row) {
            return (row as any).originalModel;
        }
        return row as T;
    }

}
